import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CalendarClock,
  CheckCircle2,
  Edit3,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  X
} from 'lucide-react';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';

const emptyForm = {
  admin_id: '',
  service_type_id: '',
  customer_id: '',
  customer_name: '',
  customer_mobile: '',
  vehicle_no: '',
  service_source: 'only_reminder',
  device_sold_by_us: false,
  external_vendor: '',
  imei: '',
  sim_no: '',
  provider_name: '',
  reference_no: '',
  document_url: '',
  start_date: '',
  expiry_date: '',
  reminder_before_days: 30,
  amount: '',
  paid_amount: '',
  status: 'upcoming',
  assigned_user_id: '',
  dealer_id: '',
  notes: ''
};

const serviceSources = [
  { key: 'sold_by_us', label: 'Device sold by us' },
  { key: 'external_device', label: 'External device' },
  { key: 'only_reminder', label: 'Only reminder' },
  { key: 'renewal_lead', label: 'Renewal lead' }
];

const serviceStatuses = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'followup_pending', label: 'Follow-up pending' },
  { key: 'quoted', label: 'Quoted' },
  { key: 'converted', label: 'Converted' },
  { key: 'active', label: 'Active' },
  { key: 'expired', label: 'Expired' },
  { key: 'lost', label: 'Lost' },
  { key: 'renewed_elsewhere', label: 'Renewed elsewhere' }
];

const vehicleServiceSelectionKeys = [
  'gps_device_renewal',
  'insurance',
  'fitness_certificate',
  'pollution_puc',
  'rto_tax',
  'number_plate',
  'permit_service',
  'national_permit',
  'state_permit',
  'road_tax',
  'fastag',
  'vehicle_service'
];

const vehicleServiceShortLabels = {
  gps_device_renewal: 'GPS Renewal',
  insurance: 'Insurance',
  fitness_certificate: 'Fitness',
  pollution_puc: 'PUC',
  rto_tax: 'RTO Tax',
  number_plate: 'Number Plate',
  permit_service: 'Permit',
  national_permit: 'National Permit',
  state_permit: 'State Permit',
  road_tax: 'Road Tax',
  fastag: 'FASTag',
  vehicle_service: 'Other Service'
};

const serviceViewStatusMap = {
  'vehicle-service-upcoming': 'upcoming',
  'vehicle-service-followup': 'followup_pending',
  'vehicle-service-quoted': 'quoted',
  'vehicle-service-converted': 'converted',
  'vehicle-service-active': 'active',
  'vehicle-service-expired': 'expired',
  'vehicle-service-lost': 'lost'
};

const serviceViewTypeMap = {
  'vehicle-service-gps-renewal': 'gps_device_renewal',
  'vehicle-service-insurance': 'insurance',
  'vehicle-service-fitness': 'fitness_certificate',
  'vehicle-service-puc': 'pollution_puc',
  'vehicle-service-permit': 'permit_service',
  'vehicle-service-fastag': 'fastag',
  'vehicle-service-other': 'vehicle_service'
};

const statusColors = {
  upcoming: { bg: '#e0f2fe', color: '#0369a1' },
  followup_pending: { bg: '#fef3c7', color: '#b45309' },
  quoted: { bg: '#ede9fe', color: '#6d28d9' },
  converted: { bg: '#dcfce7', color: '#15803d' },
  active: { bg: '#d1fae5', color: '#047857' },
  expired: { bg: '#fee2e2', color: '#b91c1c' },
  lost: { bg: '#f3f4f6', color: '#4b5563' },
  renewed_elsewhere: { bg: '#fce7f3', color: '#be185d' }
};

const inputStyle = {
  width: '100%',
  padding: '10px 11px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '13px',
  boxSizing: 'border-box',
  background: 'white',
  color: '#111827'
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  fontWeight: 700,
  color: '#374151'
};

const cardStyle = {
  background: 'white',
  borderRadius: '8px',
  padding: '16px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 1px 4px rgba(15, 23, 42, 0.06)'
};

const actionButton = {
  border: 'none',
  borderRadius: '8px',
  padding: '10px 14px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: '13px'
};

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const money = (value) => `Rs. ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const pretty = (value = '') => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
const quickServiceLabel = (type) => vehicleServiceShortLabels[type?.service_key] || type?.name || 'Service';
const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Services({ serviceSaleMode = false, onOpenServiceSale, serviceView = '' }) {
  const user = getUser();
  const isSuperAdmin = user?.role === 'superadmin';
  const canManageServiceTypes = ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role);
  const canEditServices = ['superadmin', 'admin', 'whitelabel_admin', 'employee', 'dealer'].includes(user?.role);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissionsPayload, setPermissionsPayload] = useState({ admins: [], service_types: [], permissions: [] });
  const [serviceTypePayload, setServiceTypePayload] = useState({ admins: [], service_types: [] });
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [serviceTypeForm, setServiceTypeForm] = useState({ admin_id: '', name: '', description: '', default_reminder_days: 30, is_active: true });
  const [editingServiceTypeId, setEditingServiceTypeId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissionSaving, setPermissionSaving] = useState(false);
  const [serviceTypeSaving, setServiceTypeSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({ term: '', service_type_id: '', status: '' });
  const showServiceEntryForm = canEditServices && (serviceSaleMode || Boolean(editingId));

  const loadBaseData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_URL}/api/services/types`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/services`, { headers: getHeaders() })
      ];

      if (user?.role !== 'customer') {
        requests.push(axios.get(`${API_URL}/api/customers`, { headers: getHeaders() }));
      }

      if (['superadmin', 'admin', 'whitelabel_admin', 'employee'].includes(user?.role)) {
        requests.push(axios.get(`${API_URL}/api/users`, { headers: getHeaders() }));
      }

      const responses = await Promise.all(requests);
      setServiceTypes(responses[0].data || []);
      setServices(responses[1].data || []);
      setCustomers(responses[2]?.data || []);
      setUsers(responses[3]?.data || []);
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not load services');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  const loadPermissions = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await axios.get(`${API_URL}/api/services/permissions`, { headers: getHeaders() });
      setPermissionsPayload(res.data || { admins: [], service_types: [], permissions: [] });
      setSelectedAdminId((current) => current || String(res.data?.admins?.[0]?.id || ''));
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not load service permissions');
    }
  }, [isSuperAdmin]);

  const loadServiceTypeManager = useCallback(async () => {
    if (!canManageServiceTypes) return;
    try {
      const res = await axios.get(`${API_URL}/api/services/types/manage`, { headers: getHeaders() });
      setServiceTypePayload(res.data || { admins: [], service_types: [] });
      if (isSuperAdmin && !serviceTypeForm.admin_id) {
        setServiceTypeForm((current) => ({ ...current, admin_id: current.admin_id || '' }));
      }
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not load service master');
    }
  }, [canManageServiceTypes, isSuperAdmin, serviceTypeForm.admin_id]);

  useEffect(() => {
    loadBaseData();
    loadPermissions();
    loadServiceTypeManager();
  }, [loadBaseData, loadPermissions, loadServiceTypeManager]);

  useEffect(() => {
    if (!serviceView) return;
    const status = serviceViewStatusMap[serviceView];
    const serviceKey = serviceViewTypeMap[serviceView];
    if (status) {
      setFilters((current) => ({ ...current, status }));
      return;
    }
    if (serviceKey) {
      const matchedType = serviceTypes.find((item) => item.service_key === serviceKey);
      setFilters((current) => ({
        ...current,
        service_type_id: matchedType ? String(matchedType.id) : current.service_type_id,
        status: ''
      }));
    }
  }, [serviceTypes, serviceView]);

  useEffect(() => {
    if (!selectedAdminId) {
      setSelectedPermissionIds([]);
      return;
    }
    const allowed = (permissionsPayload.permissions || [])
      .filter((item) => Number(item.admin_id) === Number(selectedAdminId))
      .map((item) => Number(item.service_type_id));
    setSelectedPermissionIds(allowed);
  }, [permissionsPayload.permissions, selectedAdminId]);

  useEffect(() => {
    if (!form.service_type_id && serviceTypes.length > 0) {
      setForm((current) => ({
        ...current,
        service_type_id: String(serviceTypes[0].id),
        reminder_before_days: serviceTypes[0].default_reminder_days || 30
      }));
    }
  }, [form.service_type_id, serviceTypes]);

  useEffect(() => {
    if (isSuperAdmin && !form.admin_id && permissionsPayload.admins?.length > 0) {
      setForm((current) => ({ ...current, admin_id: String(permissionsPayload.admins[0].id) }));
    }
  }, [form.admin_id, isSuperAdmin, permissionsPayload.admins]);

  const adminOptions = (permissionsPayload.admins?.length ? permissionsPayload.admins : serviceTypePayload.admins) || [];
  const managedServiceTypes = serviceTypePayload.service_types || [];
  const selectedAdmin = adminOptions.find((item) => Number(item.id) === Number(form.admin_id));
  const assignedUsers = useMemo(() => {
    if (isSuperAdmin && form.admin_id) {
      const direct = users.filter((item) => Number(item.created_by) === Number(form.admin_id) || Number(item.id) === Number(form.admin_id));
      const employeeIds = new Set(direct.filter((item) => item.role === 'employee').map((item) => Number(item.id)));
      return users.filter((item) => (
        Number(item.id) === Number(form.admin_id)
        || Number(item.created_by) === Number(form.admin_id)
        || employeeIds.has(Number(item.created_by))
      ) && item.role !== 'customer');
    }
    return users.filter((item) => item.role !== 'customer');
  }, [form.admin_id, isSuperAdmin, users]);
  const dealerOptions = useMemo(() => {
    const selfDealer = user?.role === 'dealer'
      ? [{ id: user.id, name: user.name || 'My Dealer Account', mobile: user.mobile, role: 'dealer' }]
      : [];
    const source = isSuperAdmin && form.admin_id ? assignedUsers : users;
    const dealers = source.filter((item) => item.role === 'dealer');
    const seen = new Set();
    return [...selfDealer, ...dealers].filter((item) => {
      const id = Number(item.id || 0);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [assignedUsers, form.admin_id, isSuperAdmin, user?.id, user?.mobile, user?.name, user?.role, users]);

  useEffect(() => {
    if (user?.role === 'dealer' && user?.id && String(form.dealer_id) !== String(user.id)) {
      setForm((current) => ({ ...current, dealer_id: String(user.id) }));
      return;
    }
    if (!form.dealer_id || dealerOptions.length === 0) return;
    if (!dealerOptions.some((item) => String(item.id) === String(form.dealer_id))) {
      setForm((current) => ({ ...current, dealer_id: '' }));
    }
  }, [dealerOptions, form.dealer_id, user?.id, user?.role]);

  const selectedType = serviceTypes.find((item) => Number(item.id) === Number(form.service_type_id));
  const quickVehicleServices = useMemo(() => (
    vehicleServiceSelectionKeys
      .map((key) => serviceTypes.find((item) => item.service_key === key))
      .filter(Boolean)
  ), [serviceTypes]);
  const permissionVehicleServiceIds = useMemo(() => (
    vehicleServiceSelectionKeys
      .map((key) => (permissionsPayload.service_types || []).find((item) => item.service_key === key)?.id)
      .filter(Boolean)
      .map(Number)
  ), [permissionsPayload.service_types]);
  const summary = useMemo(() => ({
    total: services.length,
    expiring: services.filter((item) => Number(item.days_left) >= 0 && Number(item.days_left) <= Number(item.reminder_before_days || 30)).length,
    expired: services.filter((item) => Number(item.days_left) < 0 || item.status === 'expired').length,
    converted: services.filter((item) => item.status === 'converted').length,
    balance: services.reduce((sum, item) => sum + Number(item.balance_amount || 0), 0)
  }), [services]);

  const filteredServices = useMemo(() => {
    const term = filters.term.toLowerCase().trim();
    return services.filter((item) => {
      if (filters.service_type_id && Number(item.service_type_id) !== Number(filters.service_type_id)) return false;
      if (filters.status && item.status !== filters.status) return false;
      if (!term) return true;
      return [
        item.service_name,
        item.customer_name,
        item.linked_customer_name,
        item.customer_mobile,
        item.vehicle_no,
        item.imei,
        item.reference_no,
        item.admin_name,
        item.dealer_name,
        item.dealer_mobile
      ].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [filters, services]);

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleServiceTypeChange = (value) => {
    const type = serviceTypes.find((item) => Number(item.id) === Number(value));
    setForm((current) => ({
      ...current,
      service_type_id: value,
      reminder_before_days: type?.default_reminder_days || current.reminder_before_days || 30
    }));
  };

  const handleCustomerChange = (value) => {
    const customer = customers.find((item) => Number(item.id) === Number(value));
    if (!customer) {
      setField('customer_id', '');
      return;
    }
    setForm((current) => ({
      ...current,
      customer_id: String(customer.id),
      customer_name: customer.name || '',
      customer_mobile: customer.mobile || '',
      vehicle_no: customer.vehicle_no || '',
      imei: customer.tracking_imei || customer.imei || customer.sale_imei || '',
      sim_no: customer.tracking_sim_no || customer.sim_no || customer.sale_sim_no || '',
      admin_id: isSuperAdmin ? String(customer.admin_id || current.admin_id || '') : current.admin_id,
      dealer_id: customer.dealer_id ? String(customer.dealer_id) : current.dealer_id
    }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      admin_id: isSuperAdmin ? String(adminOptions[0]?.id || '') : '',
      service_type_id: String(serviceTypes[0]?.id || ''),
      reminder_before_days: serviceTypes[0]?.default_reminder_days || 30,
      dealer_id: user?.role === 'dealer' ? String(user.id || '') : ''
    });
  };

  const editService = (item) => {
    setEditingId(item.id);
    setForm({
      admin_id: String(item.admin_id || ''),
      service_type_id: String(item.service_type_id || ''),
      customer_id: String(item.customer_id || ''),
      customer_name: item.customer_name || item.linked_customer_name || '',
      customer_mobile: item.customer_mobile || item.linked_customer_mobile || '',
      vehicle_no: item.vehicle_no || item.linked_vehicle_no || '',
      service_source: item.service_source || 'only_reminder',
      device_sold_by_us: Boolean(item.device_sold_by_us),
      external_vendor: item.external_vendor || '',
      imei: item.imei || '',
      sim_no: item.sim_no || '',
      provider_name: item.provider_name || '',
      reference_no: item.reference_no || '',
      document_url: item.document_url || '',
      start_date: toInputDate(item.start_date),
      expiry_date: toInputDate(item.expiry_date),
      reminder_before_days: item.reminder_before_days || 30,
      amount: item.amount || '',
      paid_amount: item.paid_amount || '',
      status: item.status || 'upcoming',
      assigned_user_id: String(item.assigned_user_id || ''),
      dealer_id: String(item.dealer_id || ''),
      notes: item.notes || ''
    });
  };

  const saveService = async (event) => {
    event.preventDefault();
    if (!canEditServices) return;
    if (!form.service_type_id) {
      setMessage('Service type is required');
      return;
    }
    if (isSuperAdmin && !form.admin_id) {
      setMessage('Admin workspace is required');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      const payload = {
        ...form,
        device_sold_by_us: form.device_sold_by_us || form.service_source === 'sold_by_us'
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/services/${editingId}`, payload, { headers: getHeaders() });
        setMessage('Service updated successfully');
      } else {
        await axios.post(`${API_URL}/api/services`, payload, { headers: getHeaders() });
        setMessage(serviceSaleMode ? 'Service sale saved successfully' : 'Service saved successfully');
      }
      resetForm();
      await loadBaseData();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not save service');
    } finally {
      setSaving(false);
    }
  };

  const deleteService = async (item) => {
    if (!window.confirm(`Delete ${item.service_name} for ${item.customer_name || item.linked_customer_name || item.vehicle_no || 'this record'}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/services/${item.id}`, { headers: getHeaders() });
      setMessage('Service deleted successfully');
      await loadBaseData();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not delete service');
    }
  };

  const togglePermission = (serviceTypeId) => {
    setSelectedPermissionIds((current) => (
      current.includes(Number(serviceTypeId))
        ? current.filter((id) => id !== Number(serviceTypeId))
        : [...current, Number(serviceTypeId)]
    ));
  };

  const selectVehicleServicePermissions = () => {
    setSelectedPermissionIds((current) => Array.from(new Set([...current.map(Number), ...permissionVehicleServiceIds])));
  };

  const clearVehicleServicePermissions = () => {
    setSelectedPermissionIds((current) => current.filter((id) => !permissionVehicleServiceIds.includes(Number(id))));
  };

  const savePermissions = async () => {
    if (!selectedAdminId) return;
    setPermissionSaving(true);
    setMessage('');
    try {
      await axios.put(
        `${API_URL}/api/services/permissions/${selectedAdminId}`,
        { service_type_ids: selectedPermissionIds },
        { headers: getHeaders() }
      );
      setMessage('Service permissions updated');
      await loadPermissions();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not save permissions');
    } finally {
      setPermissionSaving(false);
    }
  };

  const resetServiceTypeForm = () => {
    setEditingServiceTypeId(null);
    setServiceTypeForm({ admin_id: '', name: '', description: '', default_reminder_days: 30, is_active: true });
  };

  const editServiceType = (type) => {
    setEditingServiceTypeId(type.id);
    setServiceTypeForm({
      admin_id: String(type.admin_id || ''),
      name: type.name || '',
      description: type.description || '',
      default_reminder_days: type.default_reminder_days || 30,
      is_active: Number(type.is_active) === 1
    });
  };

  const saveServiceType = async (event) => {
    event.preventDefault();
    if (!canManageServiceTypes) return;
    if (!serviceTypeForm.name.trim()) {
      setMessage('Service name is required');
      return;
    }
    setServiceTypeSaving(true);
    setMessage('');
    try {
      const payload = {
        ...serviceTypeForm,
        default_reminder_days: Number(serviceTypeForm.default_reminder_days || 30),
        is_active: serviceTypeForm.is_active
      };
      if (editingServiceTypeId) {
        await axios.put(`${API_URL}/api/services/types/${editingServiceTypeId}`, payload, { headers: getHeaders() });
        setMessage('Service type updated');
      } else {
        await axios.post(`${API_URL}/api/services/types`, payload, { headers: getHeaders() });
        setMessage('Service type added');
      }
      resetServiceTypeForm();
      await loadServiceTypeManager();
      await loadPermissions();
      await loadBaseData();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not save service type');
    } finally {
      setServiceTypeSaving(false);
    }
  };

  const toggleServiceTypeVisibility = async (type) => {
    if (!canManageServiceTypes) return;
    try {
      if (isSuperAdmin) {
        await axios.put(
          `${API_URL}/api/services/types/${type.id}`,
          {
            name: type.name,
            description: type.description || '',
            default_reminder_days: type.default_reminder_days || 30,
            is_active: Number(type.is_active) !== 1
          },
          { headers: getHeaders() }
        );
      } else {
        await axios.patch(
          `${API_URL}/api/services/types/${type.id}/visibility`,
          { is_enabled: Number(type.allowed) !== 1 },
          { headers: getHeaders() }
        );
      }
      await loadServiceTypeManager();
      await loadBaseData();
      await loadPermissions();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not update service visibility');
    }
  };

  const renderDayBadge = (item) => {
    if (!item.expiry_date) return <span style={{ color: '#6b7280' }}>No expiry</span>;
    const days = Number(item.days_left || 0);
    const color = days < 0
      ? { bg: '#fee2e2', color: '#b91c1c' }
      : days <= 7
        ? { bg: '#ffedd5', color: '#c2410c' }
        : { bg: '#dcfce7', color: '#15803d' };
    const label = days < 0 ? `${Math.abs(days)} overdue` : days === 0 ? 'Today' : `${days} days`;
    return <span style={{ background: color.bg, color: color.color, padding: '4px 9px', borderRadius: '999px', fontSize: '12px', fontWeight: 700 }}>{label}</span>;
  };
  const pageTitle = serviceSaleMode ? 'Service Sale' : 'Vehicle Services';
  const pageDescription = serviceSaleMode
    ? 'Create vehicle service billing, payment, and expiry entries here.'
    : 'GPS renewal, insurance, fitness, PUC, permit, tax, FASTag and other vehicle service expiry work.';
  const serviceFormTitle = serviceSaleMode
    ? (editingId ? 'Update Service Sale' : 'Create Service Sale')
    : 'Update Service';

  return (
    <div className="fixed-panel-page tight" style={{ color: '#111827' }}>
      <div className="fixed-panel-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#111827' }}>{pageTitle}</h2>
          <p style={{ margin: '5px 0 0', color: '#6b7280', fontSize: '13px' }}>
            {pageDescription}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {!serviceSaleMode && onOpenServiceSale && (
            <button type="button" onClick={onOpenServiceSale} style={{ ...actionButton, background: '#0f766e', color: 'white' }}>
              <Plus size={16} /> Service Sale
            </button>
          )}
          <button type="button" onClick={() => { loadBaseData(); loadPermissions(); loadServiceTypeManager(); }} style={{ ...actionButton, background: '#1e3a5f', color: 'white' }}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>
      </div>

      <div className="fixed-panel-body">
      {message && (
        <div style={{ ...cardStyle, marginBottom: '16px', borderColor: '#bfdbfe', background: '#eff6ff', color: '#1d4ed8' }}>
          {message}
        </div>
      )}

      {!serviceSaleMode && canManageServiceTypes && (
        <section style={{ ...cardStyle, marginBottom: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <div>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#111827' }}>
                <Plus size={19} /> Service Master
              </h3>
              <p style={{ margin: '5px 0 0', color: '#6b7280', fontSize: '13px' }}>
                Add, edit, and show or hide services from this portal.
              </p>
            </div>
            {editingServiceTypeId && (
              <button type="button" onClick={resetServiceTypeForm} style={{ ...actionButton, background: '#f3f4f6', color: '#374151' }}>
                <X size={16} /> Cancel Edit
              </button>
            )}
          </div>

          <form onSubmit={saveServiceType} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '16px', alignItems: 'end' }}>
            {isSuperAdmin && (
              <label>
                <span style={labelStyle}>Service Scope</span>
                <select value={serviceTypeForm.admin_id} onChange={(event) => setServiceTypeForm((current) => ({ ...current, admin_id: event.target.value }))} style={inputStyle} disabled={Boolean(editingServiceTypeId)}>
                  <option value="">Global service</option>
                  {adminOptions.map((admin) => (
                    <option key={admin.id} value={admin.id}>{admin.name} - {pretty(admin.role)}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              <span style={labelStyle}>Service Name</span>
              <input value={serviceTypeForm.name} onChange={(event) => setServiceTypeForm((current) => ({ ...current, name: event.target.value }))} style={inputStyle} placeholder="Number Plate Service" />
            </label>
            <label>
              <span style={labelStyle}>Reminder Days</span>
              <input type="number" min="1" value={serviceTypeForm.default_reminder_days} onChange={(event) => setServiceTypeForm((current) => ({ ...current, default_reminder_days: event.target.value }))} style={inputStyle} />
            </label>
            <label style={{ gridColumn: 'span 2' }}>
              <span style={labelStyle}>Description</span>
              <input value={serviceTypeForm.description} onChange={(event) => setServiceTypeForm((current) => ({ ...current, description: event.target.value }))} style={inputStyle} placeholder="Service details" />
            </label>
            {isSuperAdmin && (
              <label>
                <span style={labelStyle}>Status</span>
                <select value={serviceTypeForm.is_active ? '1' : '0'} onChange={(event) => setServiceTypeForm((current) => ({ ...current, is_active: event.target.value === '1' }))} style={inputStyle}>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </label>
            )}
            <button type="submit" disabled={serviceTypeSaving} style={{ ...actionButton, justifyContent: 'center', background: '#0f766e', color: 'white', opacity: serviceTypeSaving ? 0.7 : 1 }}>
              <Save size={16} /> {serviceTypeSaving ? 'Saving...' : editingServiceTypeId ? 'Update Type' : 'Add Type'}
            </button>
          </form>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '10px' }}>
            {managedServiceTypes.map((type) => {
              const isOwnCustom = Number(type.admin_id || 0) !== 0;
              const canEditType = isSuperAdmin || (isOwnCustom && Number(type.allowed) === 1);
              const enabled = isSuperAdmin ? Number(type.is_active) === 1 : Number(type.allowed) === 1;
              return (
                <div key={type.id} style={{ border: `1px solid ${enabled ? '#bbf7d0' : '#fecaca'}`, borderRadius: '8px', padding: '12px', background: enabled ? '#f0fdf4' : '#fef2f2' }}>
                  <strong style={{ display: 'block', color: '#111827', fontSize: '13px' }}>{type.name}</strong>
                  <small style={{ display: 'block', color: '#64748b', marginTop: '3px' }}>
                    {Number(type.admin_id || 0) === 0 ? 'Global' : (type.admin_name || 'Custom')} | {type.default_reminder_days || 30} days
                  </small>
                  {type.description && <small style={{ display: 'block', color: '#64748b', marginTop: '5px' }}>{type.description}</small>}
                  <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', marginTop: '10px' }}>
                    <button type="button" onClick={() => toggleServiceTypeVisibility(type)} style={{ ...actionButton, padding: '7px 10px', background: enabled ? '#fee2e2' : '#dcfce7', color: enabled ? '#b91c1c' : '#15803d' }}>
                      {enabled ? (isSuperAdmin ? 'Inactive' : 'Hide') : (isSuperAdmin ? 'Active' : 'Show')}
                    </button>
                    {canEditType && (
                      <button type="button" onClick={() => editServiceType(type)} style={{ ...actionButton, padding: '7px 10px', background: '#e0f2fe', color: '#0369a1' }}>
                        <Edit3 size={14} /> Edit
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {managedServiceTypes.length === 0 && <div style={{ color: '#64748b', fontSize: '13px' }}>No service types found.</div>}
          </div>
        </section>
      )}

      {!serviceSaleMode && isSuperAdmin && (
        <section style={{ ...cardStyle, marginBottom: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <div>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#111827' }}>
                <ShieldCheck size={19} /> Service Permission
              </h3>
              <p style={{ margin: '5px 0 0', color: '#6b7280', fontSize: '13px' }}>
                Selected admin can work only on checked services.
              </p>
            </div>
            <button type="button" onClick={savePermissions} disabled={!selectedAdminId || permissionSaving} style={{ ...actionButton, background: '#0f766e', color: 'white', opacity: permissionSaving ? 0.7 : 1 }}>
              <Save size={16} /> {permissionSaving ? 'Saving...' : 'Save Permission'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <button type="button" onClick={selectVehicleServicePermissions} disabled={!selectedAdminId || permissionVehicleServiceIds.length === 0} style={{ ...actionButton, padding: '8px 11px', background: '#e0f2fe', color: '#0369a1' }}>
              Select Vehicle Services
            </button>
            <button type="button" onClick={clearVehicleServicePermissions} disabled={!selectedAdminId || permissionVehicleServiceIds.length === 0} style={{ ...actionButton, padding: '8px 11px', background: '#f3f4f6', color: '#374151' }}>
              Clear Vehicle Services
            </button>
            <span style={{ alignSelf: 'center', color: '#64748b', fontSize: '12px', fontWeight: 700 }}>
              GPS renewal, insurance, fitness, PUC, permit, tax, FASTag and other services
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) 1fr', gap: '16px', alignItems: 'start' }}>
            <label>
              <span style={labelStyle}>Admin / Whitelabel Admin</span>
              <select value={selectedAdminId} onChange={(event) => setSelectedAdminId(event.target.value)} style={inputStyle}>
                <option value="">Select admin</option>
                {adminOptions.map((admin) => (
                  <option key={admin.id} value={admin.id}>{admin.name} - {pretty(admin.role)}</option>
                ))}
              </select>
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px' }}>
              {(permissionsPayload.service_types || []).map((type) => {
                const checked = selectedPermissionIds.includes(Number(type.id));
                return (
                  <label key={type.id} style={{ border: `1px solid ${checked ? '#0f766e' : '#e5e7eb'}`, borderRadius: '8px', padding: '11px', display: 'flex', gap: '9px', background: checked ? '#ecfdf5' : 'white', cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => togglePermission(type.id)} />
                    <span>
                      <strong style={{ display: 'block', fontSize: '13px', color: '#111827' }}>{type.name}</strong>
                      <small style={{ color: '#6b7280' }}>{type.default_reminder_days || 30} day reminder</small>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '18px' }}>
        {[
          { label: 'Total Services', value: summary.total, color: '#2563eb' },
          { label: 'Expiring', value: summary.expiring, color: '#c2410c' },
          { label: 'Expired', value: summary.expired, color: '#b91c1c' },
          { label: 'Converted', value: summary.converted, color: '#15803d' },
          { label: 'Balance', value: money(summary.balance), color: '#6d28d9' }
        ].map((item) => (
          <div key={item.label} style={{ ...cardStyle, borderTop: `4px solid ${item.color}` }}>
            <span style={{ display: 'block', color: '#6b7280', fontSize: '12px', marginBottom: '7px' }}>{item.label}</span>
            <strong style={{ fontSize: '24px', color: item.color }}>{item.value}</strong>
          </div>
        ))}
      </div>

      {showServiceEntryForm && (
        <section style={{ ...cardStyle, marginBottom: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#111827' }}>
              {editingId ? <Edit3 size={18} /> : <Plus size={18} />} {serviceFormTitle}
            </h3>
            {editingId && (
              <button type="button" onClick={resetForm} style={{ ...actionButton, background: '#f3f4f6', color: '#374151' }}>
                <X size={15} /> Cancel
              </button>
            )}
          </div>

          <form onSubmit={saveService}>
            {quickVehicleServices.length > 0 && (
              <div style={{ marginBottom: '14px', padding: '12px', border: '1px solid #dbeafe', borderRadius: '8px', background: '#f8fafc' }}>
                <span style={{ display: 'block', marginBottom: '8px', color: '#1e3a5f', fontSize: '13px', fontWeight: 800 }}>Quick Service Selection</span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {quickVehicleServices.map((type) => {
                    const active = Number(form.service_type_id) === Number(type.id);
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => handleServiceTypeChange(String(type.id))}
                        style={{
                          border: `1px solid ${active ? '#0f766e' : '#cbd5e1'}`,
                          borderRadius: '8px',
                          background: active ? '#ecfdf5' : 'white',
                          color: active ? '#047857' : '#334155',
                          padding: '8px 11px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 800
                        }}
                      >
                        {quickServiceLabel(type)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px' }}>
              {isSuperAdmin && (
                <label>
                  <span style={labelStyle}>Admin Workspace</span>
                  <select value={form.admin_id} onChange={(event) => setField('admin_id', event.target.value)} style={inputStyle} required>
                    <option value="">Select admin</option>
                    {adminOptions.map((admin) => (
                      <option key={admin.id} value={admin.id}>{admin.name} - {pretty(admin.role)}</option>
                    ))}
                  </select>
                </label>
              )}

              <label>
                <span style={labelStyle}>Service Type</span>
                <select value={form.service_type_id} onChange={(event) => handleServiceTypeChange(event.target.value)} style={inputStyle} required>
                  <option value="">Select service</option>
                  {serviceTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </label>

              <label>
                <span style={labelStyle}>Customer</span>
                <select value={form.customer_id} onChange={(event) => handleCustomerChange(event.target.value)} style={inputStyle}>
                  <option value="">Manual entry</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name} - {customer.mobile}</option>
                  ))}
                </select>
              </label>

              <label>
                <span style={labelStyle}>Customer Name</span>
                <input value={form.customer_name} onChange={(event) => setField('customer_name', event.target.value)} style={inputStyle} placeholder="Customer name" />
              </label>

              <label>
                <span style={labelStyle}>Mobile</span>
                <input value={form.customer_mobile} onChange={(event) => setField('customer_mobile', event.target.value)} style={inputStyle} placeholder="Mobile" />
              </label>

              <label>
                <span style={labelStyle}>Vehicle No</span>
                <input value={form.vehicle_no} onChange={(event) => setField('vehicle_no', event.target.value.toUpperCase())} style={inputStyle} placeholder="Vehicle no" />
              </label>

              <label>
                <span style={labelStyle}>Source</span>
                <select value={form.service_source} onChange={(event) => setField('service_source', event.target.value)} style={inputStyle}>
                  {serviceSources.map((source) => (
                    <option key={source.key} value={source.key}>{source.label}</option>
                  ))}
                </select>
              </label>

              <label>
                <span style={labelStyle}>IMEI / Device</span>
                <input value={form.imei} onChange={(event) => setField('imei', event.target.value)} style={inputStyle} placeholder="IMEI" />
              </label>

              <label>
                <span style={labelStyle}>SIM No</span>
                <input value={form.sim_no} onChange={(event) => setField('sim_no', event.target.value)} style={inputStyle} placeholder="SIM no" />
              </label>

              <label>
                <span style={labelStyle}>Provider / Company</span>
                <input value={form.provider_name} onChange={(event) => setField('provider_name', event.target.value)} style={inputStyle} placeholder="Provider" />
              </label>

              <label>
                <span style={labelStyle}>External Vendor</span>
                <input value={form.external_vendor} onChange={(event) => setField('external_vendor', event.target.value)} style={inputStyle} placeholder="Vendor" />
              </label>

              <label>
                <span style={labelStyle}>Reference No</span>
                <input value={form.reference_no} onChange={(event) => setField('reference_no', event.target.value)} style={inputStyle} placeholder="Policy, permit or invoice no" />
              </label>

              <label>
                <span style={labelStyle}>Start Date</span>
                <input type="date" value={form.start_date} onChange={(event) => setField('start_date', event.target.value)} style={inputStyle} />
              </label>

              <label>
                <span style={labelStyle}>Expiry Date</span>
                <input type="date" value={form.expiry_date} onChange={(event) => setField('expiry_date', event.target.value)} style={inputStyle} />
              </label>

              <label>
                <span style={labelStyle}>Reminder Days</span>
                <input type="number" min="1" value={form.reminder_before_days} onChange={(event) => setField('reminder_before_days', event.target.value)} style={inputStyle} />
              </label>

              <label>
                <span style={labelStyle}>Amount</span>
                <input type="number" min="0" value={form.amount} onChange={(event) => setField('amount', event.target.value)} style={inputStyle} placeholder="0" />
              </label>

              <label>
                <span style={labelStyle}>Paid</span>
                <input type="number" min="0" value={form.paid_amount} onChange={(event) => setField('paid_amount', event.target.value)} style={inputStyle} placeholder="0" />
              </label>

              <label>
                <span style={labelStyle}>Status</span>
                <select value={form.status} onChange={(event) => setField('status', event.target.value)} style={inputStyle}>
                  {serviceStatuses.map((status) => (
                    <option key={status.key} value={status.key}>{status.label}</option>
                  ))}
                </select>
              </label>

              <label>
                <span style={labelStyle}>Tag Dealer</span>
                <select
                  value={form.dealer_id}
                  onChange={(event) => setField('dealer_id', event.target.value)}
                  style={inputStyle}
                  disabled={user?.role === 'dealer'}
                >
                  <option value="">Direct / No Dealer</option>
                  {dealerOptions.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}{item.mobile ? ` - ${item.mobile}` : ''}</option>
                  ))}
                </select>
              </label>

              <label>
                <span style={labelStyle}>Assigned To</span>
                <select value={form.assigned_user_id} onChange={(event) => setField('assigned_user_id', event.target.value)} style={inputStyle}>
                  <option value="">No assignment</option>
                  {assignedUsers.map((item) => (
                    <option key={item.id} value={item.id}>{item.name} - {pretty(item.role)}</option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginTop: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontSize: '13px', fontWeight: 700 }}>
                <input type="checkbox" checked={form.device_sold_by_us || form.service_source === 'sold_by_us'} onChange={(event) => setField('device_sold_by_us', event.target.checked)} />
                Device sold by us
              </label>

              <label>
                <span style={labelStyle}>Document URL</span>
                <input value={form.document_url} onChange={(event) => setField('document_url', event.target.value)} style={inputStyle} placeholder="Document link" />
              </label>

              <label>
                <span style={labelStyle}>Notes</span>
                <textarea value={form.notes} onChange={(event) => setField('notes', event.target.value)} style={{ ...inputStyle, minHeight: '74px', resize: 'vertical' }} placeholder={`${selectedType?.name || 'Service'} notes${selectedAdmin ? ` for ${selectedAdmin.name}` : ''}`} />
              </label>
            </div>

            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" disabled={saving || serviceTypes.length === 0} style={{ ...actionButton, background: '#1e3a5f', color: 'white', opacity: saving ? 0.7 : 1 }}>
                <Save size={16} /> {saving ? 'Saving...' : editingId ? 'Update Service' : serviceSaleMode ? 'Save Service Sale' : 'Save Service'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: '#111827' }}>
            <CalendarClock size={18} /> Service Ledger
          </h3>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input value={filters.term} onChange={(event) => setFilters((current) => ({ ...current, term: event.target.value }))} style={{ ...inputStyle, width: '220px' }} placeholder="Search customer, vehicle, IMEI" />
            <select value={filters.service_type_id} onChange={(event) => setFilters((current) => ({ ...current, service_type_id: event.target.value }))} style={{ ...inputStyle, width: '180px' }}>
              <option value="">All services</option>
              {serviceTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
            </select>
            <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} style={{ ...inputStyle, width: '180px' }}>
              <option value="">All status</option>
              {serviceStatuses.map((status) => <option key={status.key} value={status.key}>{status.label}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '34px', textAlign: 'center', color: '#6b7280' }}>Loading services...</div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1140px' }}>
              <thead>
                <tr style={{ background: '#1e3a5f', color: 'white' }}>
                  {['Service', 'Customer', 'Device', 'Expiry', 'Amount', 'Paid', 'Balance', 'Source', 'Status', 'Dealer', 'Assigned', 'Action'].map((head) => (
                    <th key={head} style={{ padding: '11px 12px', textAlign: 'left', fontSize: '12px' }}>{head}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredServices.length === 0 ? (
                  <tr>
                    <td colSpan="12" style={{ padding: '34px', textAlign: 'center', color: '#6b7280' }}>
                      No service records found.
                    </td>
                  </tr>
                ) : filteredServices.map((item, index) => {
                  const statusColor = statusColors[item.status] || statusColors.upcoming;
                  return (
                    <tr key={item.id} style={{ background: index % 2 === 0 ? 'white' : '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '11px 12px', verticalAlign: 'top' }}>
                        <strong style={{ display: 'block', color: '#111827' }}>{item.service_name}</strong>
                        <small style={{ color: '#6b7280' }}>{item.reference_no || item.provider_name || '-'}</small>
                      </td>
                      <td style={{ padding: '11px 12px', verticalAlign: 'top' }}>
                        <strong style={{ display: 'block', color: '#111827' }}>{item.customer_name || item.linked_customer_name || '-'}</strong>
                        <small style={{ color: '#6b7280' }}>{item.customer_mobile || item.linked_customer_mobile || '-'} {item.vehicle_no ? `- ${item.vehicle_no}` : ''}</small>
                      </td>
                      <td style={{ padding: '11px 12px', verticalAlign: 'top', fontSize: '13px' }}>
                        <span>{item.imei || '-'}</span>
                        <small style={{ display: 'block', color: '#6b7280' }}>{item.sim_no || ''}</small>
                      </td>
                      <td style={{ padding: '11px 12px', verticalAlign: 'top' }}>
                        <span style={{ display: 'block', fontSize: '13px', marginBottom: '5px' }}>{item.expiry_date ? formatDate(item.expiry_date) : '-'}</span>
                        {renderDayBadge(item)}
                      </td>
                      <td style={{ padding: '11px 12px', fontSize: '13px' }}>{money(item.amount)}</td>
                      <td style={{ padding: '11px 12px', fontSize: '13px' }}>{money(item.paid_amount)}</td>
                      <td style={{ padding: '11px 12px', fontSize: '13px', fontWeight: 700 }}>{money(item.balance_amount)}</td>
                      <td style={{ padding: '11px 12px', fontSize: '13px' }}>{pretty(item.service_source)}</td>
                      <td style={{ padding: '11px 12px' }}>
                        <span style={{ background: statusColor.bg, color: statusColor.color, padding: '4px 9px', borderRadius: '999px', fontSize: '12px', fontWeight: 700 }}>
                          {pretty(item.status)}
                        </span>
                      </td>
                      <td style={{ padding: '11px 12px', fontSize: '13px' }}>
                        <strong style={{ display: 'block', color: '#111827' }}>{item.dealer_name || '-'}</strong>
                        <small style={{ color: '#6b7280' }}>{item.dealer_mobile || ''}</small>
                      </td>
                      <td style={{ padding: '11px 12px', fontSize: '13px' }}>{item.assigned_user_name || item.admin_name || '-'}</td>
                      <td style={{ padding: '11px 12px' }}>
                        {canEditServices && (
                          <div style={{ display: 'flex', gap: '7px' }}>
                            <button type="button" onClick={() => editService(item)} title="Edit service" style={{ ...actionButton, padding: '8px', background: '#e0f2fe', color: '#0369a1' }}>
                              <Edit3 size={15} />
                            </button>
                            {['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role) && (
                              <button type="button" onClick={() => deleteService(item)} title="Delete service" style={{ ...actionButton, padding: '8px', background: '#fee2e2', color: '#b91c1c' }}>
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#047857', fontSize: '12px', fontWeight: 700, marginTop: '14px' }}>
        <CheckCircle2 size={15} /> {serviceTypes.length} assigned service type{serviceTypes.length === 1 ? '' : 's'} available
      </div>
      </div>
    </div>
  );
}
